import { useCallback } from 'react'

import { useMutation } from '@apollo/client/react'

import { toast } from '@buffer-mono/popcorn'

import { useAccount } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import { getDebugLogger } from '~publish/legacy/utils/getDebugLogger'

const debugLog = getDebugLogger('SetBufferHomeAsDefault')

// Mirrors the messages shown by the Settings → Preferences landing-page setting
// so the coachmark's "Set As Default" gives identical feedback.
const SUCCESS_MESSAGE = 'Great! Your preferences have been saved'
const ERROR_MESSAGE =
  'Sorry! There was a problem updating your landing page. Please try again.'

const SetBufferHomeAsDefaultLandingPage = graphql(/* GraphQL */ `
  mutation SetBufferHomeAsDefaultLandingPage(
    $input: UpdateAccountPreferencesInput!
  ) {
    updateAccountPreferences(input: $input) {
      __typename
      ... on UpdateAccountPreferencesSuccess {
        preferences {
          landingPage
        }
      }
    }
  }
`)

/**
 * Returns a callback that sets Buffer Home as the user's default landing page.
 * Backs the coachmark's "Set As Default" action. The Apollo cache is updated
 * optimistically so dependent reads (e.g. {@link useDefaultLandingPage}) reflect
 * `home` immediately — which also hides the coachmark via its gate. Surfaces the
 * same success/error toast as the Settings → Preferences landing-page setting.
 */
export const useSetBufferHomeAsDefault = (): (() => void) => {
  const { account } = useAccount()
  const [setLandingPage] = useMutation(SetBufferHomeAsDefaultLandingPage, {
    variables: { input: { landingPage: 'home' } },
    update: (cache) => {
      const accountId = account?.id
      if (!accountId) {
        return
      }
      cache.modify({
        id: cache.identify({ __typename: 'Account', id: String(accountId) }),
        fields: {
          // Only change `landingPage`; preserve the rest of the cached
          // preferences instead of rebuilding them with defaults.
          preferences(existingPreferences = { __typename: 'Preferences' }) {
            return {
              ...existingPreferences,
              landingPage: 'home',
            }
          },
        },
      })
    },
  })

  return useCallback((): void => {
    setLandingPage()
      .then((result) => {
        const response = result.data?.updateAccountPreferences
        if (response?.__typename === 'UpdateAccountPreferencesSuccess') {
          toast.success(SUCCESS_MESSAGE)
        } else {
          debugLog(response)
          toast.error(ERROR_MESSAGE)
        }
        return undefined
      })
      .catch((error: unknown) => {
        debugLog(error)
        toast.error(ERROR_MESSAGE)
      })
  }, [setLandingPage])
}
